// ======================== BUDGET MANAGEMENT PAGE ========================
// Page de gestion du budget - Admin uniquement
// Avec graphiques, alertes et configuration

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    AlertTriangle, TrendingUp, TrendingDown, Wallet, Calendar,
    Settings, Bell, CheckCircle, XCircle, DollarSign, PieChart,
    BarChart3, ArrowUpRight, ArrowDownRight, RefreshCw, Shield,
    Package, ShoppingCart, Clock, AlertCircle
} from 'lucide-react';

// Format CFA
const formatCFA = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '0 F';
    return new Intl.NumberFormat('fr-FR').format(Math.round(amount)) + ' F';
};

// ======================== MAIN COMPONENT ========================

const BudgetManagementPage = () => {
    const { api, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState(null);
    const [summary, setSummary] = useState(null);
    const [history, setHistory] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [period, setPeriod] = useState('month');
    
    // Dialogs
    const [configDialogOpen, setConfigDialogOpen] = useState(false);
    const [configForm, setConfigForm] = useState({
        total_budget: 0,
        monthly_budget: 0,
        alert_threshold: 80,
        auto_approval_limit: 50000
    });
    
    const isAdmin = user?.role === 'ADMIN';

    // ======================== DATA FETCHING ========================

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [configRes, summaryRes, historyRes, alertsRes] = await Promise.all([
                api().get('/budget/config'),
                api().get(`/budget/summary?period=${period}`),
                api().get('/budget/history?months=12'),
                api().get('/budget/alerts')
            ]);
            setConfig(configRes.data);
            setSummary(summaryRes.data);
            setHistory(historyRes.data);
            setAlerts(alertsRes.data);
            setConfigForm({
                total_budget: configRes.data.total_budget || 0,
                monthly_budget: configRes.data.monthly_budget || 0,
                alert_threshold: configRes.data.alert_threshold || 80,
                auto_approval_limit: configRes.data.auto_approval_limit || 50000
            });
        } catch (error) {
            console.error('Fetch error:', error);
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    }, [api, period]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // ======================== HANDLERS ========================

    const handleSaveConfig = async () => {
        try {
            await api().put('/budget/config', {
                total_budget: parseFloat(configForm.total_budget) || 0,
                monthly_budget: parseFloat(configForm.monthly_budget) || 0,
                alert_threshold: parseFloat(configForm.alert_threshold) || 80,
                auto_approval_limit: parseFloat(configForm.auto_approval_limit) || 50000
            });
            toast.success('Configuration mise à jour');
            setConfigDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        }
    };

    const resolveAlert = async (alertId) => {
        try {
            await api().post(`/budget/alerts/${alertId}/resolve`);
            toast.success('Alerte résolue');
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de la résolution');
        }
    };

    // ======================== COMPUTED VALUES ========================

    const budgetStatus = useMemo(() => {
        if (!summary) return { color: 'gray', label: 'Chargement...' };
        const pct = summary.percentage_used || 0;
        if (pct >= 100) return { color: 'red', label: 'Dépassé', icon: AlertTriangle };
        if (pct >= 80) return { color: 'orange', label: 'Attention', icon: AlertCircle };
        if (pct >= 50) return { color: 'yellow', label: 'Normal', icon: TrendingUp };
        return { color: 'green', label: 'Confortable', icon: CheckCircle };
    }, [summary]);

    // ======================== RENDER ========================

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="p-4 max-w-7xl mx-auto space-y-6" data-testid="budget-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Wallet className="h-7 w-7" />
                        Gestion du Budget
                    </h1>
                    <p className="text-muted-foreground text-sm">
                        Suivi et contrôle des dépenses du restaurant
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={fetchData}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Actualiser
                    </Button>
                    {isAdmin && (
                        <Button onClick={() => setConfigDialogOpen(true)}>
                            <Settings className="h-4 w-4 mr-2" />
                            Configuration
                        </Button>
                    )}
                </div>
            </div>

            {/* Period Selector */}
            <div className="flex gap-2">
                <Button 
                    variant={period === 'month' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => setPeriod('month')}
                >
                    Ce mois
                </Button>
                <Button 
                    variant={period === 'quarter' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => setPeriod('quarter')}
                >
                    Ce trimestre
                </Button>
                <Button 
                    variant={period === 'year' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => setPeriod('year')}
                >
                    Cette année
                </Button>
            </div>

            {/* Main Budget Card */}
            <Card className="border-2">
                <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">
                            Budget {summary?.period_label || ''}
                        </CardTitle>
                        <Badge 
                            variant="outline" 
                            className={`
                                ${budgetStatus.color === 'green' ? 'bg-green-100 text-green-700 border-green-300' : ''}
                                ${budgetStatus.color === 'yellow' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' : ''}
                                ${budgetStatus.color === 'orange' ? 'bg-orange-100 text-orange-700 border-orange-300' : ''}
                                ${budgetStatus.color === 'red' ? 'bg-red-100 text-red-700 border-red-300' : ''}
                            `}
                        >
                            {budgetStatus.label}
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {/* Progress Bar */}
                        <div>
                            <div className="flex justify-between text-sm mb-2">
                                <span>Utilisé: {formatCFA(summary?.total_spent || 0)}</span>
                                <span>{summary?.percentage_used || 0}%</span>
                            </div>
                            <Progress 
                                value={Math.min(summary?.percentage_used || 0, 100)} 
                                className={`h-4 ${summary?.percentage_used >= 100 ? '[&>div]:bg-red-500' : summary?.percentage_used >= 80 ? '[&>div]:bg-orange-500' : ''}`}
                            />
                            <div className="flex justify-between text-sm mt-2 text-muted-foreground">
                                <span>Budget: {formatCFA(summary?.budget_limit || 0)}</span>
                                <span>Reste: {formatCFA(summary?.remaining || 0)}</span>
                            </div>
                        </div>

                        <Separator />

                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center p-3 bg-muted/50 rounded-lg">
                                <DollarSign className="h-5 w-5 mx-auto mb-1 text-green-600" />
                                <div className="text-xl font-bold">{formatCFA(summary?.budget_limit || 0)}</div>
                                <div className="text-xs text-muted-foreground">Budget total</div>
                            </div>
                            <div className="text-center p-3 bg-muted/50 rounded-lg">
                                <ShoppingCart className="h-5 w-5 mx-auto mb-1 text-blue-600" />
                                <div className="text-xl font-bold">{formatCFA(summary?.total_spent || 0)}</div>
                                <div className="text-xs text-muted-foreground">Dépensé</div>
                            </div>
                            <div className="text-center p-3 bg-muted/50 rounded-lg">
                                <Wallet className="h-5 w-5 mx-auto mb-1 text-purple-600" />
                                <div className={`text-xl font-bold ${(summary?.remaining || 0) < 0 ? 'text-red-600' : ''}`}>
                                    {formatCFA(summary?.remaining || 0)}
                                </div>
                                <div className="text-xs text-muted-foreground">Disponible</div>
                            </div>
                            <div className="text-center p-3 bg-muted/50 rounded-lg">
                                <Package className="h-5 w-5 mx-auto mb-1 text-orange-600" />
                                <div className="text-xl font-bold">{summary?.lists_count || 0}</div>
                                <div className="text-xs text-muted-foreground">Listes finalisées</div>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Alerts */}
            {alerts.length > 0 && (
                <Card className="border-orange-300 bg-orange-50">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
                            <Bell className="h-5 w-5" />
                            Alertes ({alerts.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {alerts.slice(0, 5).map((alert) => (
                                <div 
                                    key={alert.id} 
                                    className={`flex items-center justify-between p-3 rounded-lg ${
                                        alert.level === 'critical' ? 'bg-red-100' :
                                        alert.level === 'warning' ? 'bg-orange-100' : 'bg-blue-100'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        {alert.level === 'critical' ? (
                                            <AlertTriangle className="h-5 w-5 text-red-600" />
                                        ) : alert.level === 'warning' ? (
                                            <AlertCircle className="h-5 w-5 text-orange-600" />
                                        ) : (
                                            <Bell className="h-5 w-5 text-blue-600" />
                                        )}
                                        <div>
                                            <div className="font-medium text-sm">{alert.title}</div>
                                            <div className="text-xs text-muted-foreground">{alert.message}</div>
                                        </div>
                                    </div>
                                    {alert.type === 'price' && (
                                        <Button size="sm" variant="ghost" onClick={() => resolveAlert(alert.id)}>
                                            <CheckCircle className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Tabs: Historique & Secteurs */}
            <Tabs defaultValue="history" className="w-full">
                <TabsList className="w-full sm:w-auto">
                    <TabsTrigger value="history" className="flex-1 sm:flex-none">
                        <BarChart3 className="h-4 w-4 mr-2" />
                        Historique
                    </TabsTrigger>
                    <TabsTrigger value="sectors" className="flex-1 sm:flex-none">
                        <PieChart className="h-4 w-4 mr-2" />
                        Par secteur
                    </TabsTrigger>
                    <TabsTrigger value="config" className="flex-1 sm:flex-none">
                        <Settings className="h-4 w-4 mr-2" />
                        Configuration
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="history" className="mt-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Historique des dépenses</CardTitle>
                            <CardDescription>Évolution sur les 12 derniers mois</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {history.map((month, idx) => {
                                    const maxSpent = Math.max(...history.map(h => h.total_spent || 0), 1);
                                    const pct = ((month.total_spent || 0) / maxSpent) * 100;
                                    const budgetPct = config?.monthly_budget ? 
                                        ((month.total_spent || 0) / config.monthly_budget) * 100 : 0;
                                    
                                    return (
                                        <div key={idx} className="flex items-center gap-4">
                                            <div className="w-20 text-sm font-medium">{month.label}</div>
                                            <div className="flex-1">
                                                <div className="h-6 bg-muted rounded-full overflow-hidden">
                                                    <div 
                                                        className={`h-full rounded-full transition-all ${
                                                            budgetPct >= 100 ? 'bg-red-500' :
                                                            budgetPct >= 80 ? 'bg-orange-500' : 'bg-primary'
                                                        }`}
                                                        style={{ width: `${pct}%` }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="w-28 text-right">
                                                <div className="text-sm font-medium">{formatCFA(month.total_spent || 0)}</div>
                                                <div className="text-xs text-muted-foreground">{month.lists_count} liste(s)</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="sectors" className="mt-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Dépenses par secteur</CardTitle>
                            <CardDescription>Répartition pour {summary?.period_label}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {summary?.by_sector && Object.keys(summary.by_sector).length > 0 ? (
                                <div className="space-y-3">
                                    {Object.entries(summary.by_sector).map(([sector, amount]) => {
                                        const total = Object.values(summary.by_sector).reduce((a, b) => a + b, 0);
                                        const pct = total > 0 ? (amount / total) * 100 : 0;
                                        
                                        return (
                                            <div key={sector} className="flex items-center gap-4">
                                                <div className="w-24 text-sm font-medium capitalize">{sector}</div>
                                                <div className="flex-1">
                                                    <div className="h-6 bg-muted rounded-full overflow-hidden">
                                                        <div 
                                                            className="h-full bg-primary rounded-full"
                                                            style={{ width: `${pct}%` }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="w-28 text-right">
                                                    <div className="text-sm font-medium">{formatCFA(amount)}</div>
                                                    <div className="text-xs text-muted-foreground">{pct.toFixed(1)}%</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    Aucune dépense pour cette période
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="config" className="mt-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Configuration actuelle</CardTitle>
                            <CardDescription>Paramètres du budget</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div className="p-4 bg-muted/50 rounded-lg">
                                        <div className="text-sm text-muted-foreground">Budget annuel</div>
                                        <div className="text-2xl font-bold">{formatCFA(config?.total_budget || 0)}</div>
                                    </div>
                                    <div className="p-4 bg-muted/50 rounded-lg">
                                        <div className="text-sm text-muted-foreground">Budget mensuel</div>
                                        <div className="text-2xl font-bold">{formatCFA(config?.monthly_budget || 0)}</div>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="p-4 bg-muted/50 rounded-lg">
                                        <div className="text-sm text-muted-foreground">Seuil d'alerte</div>
                                        <div className="text-2xl font-bold">{config?.alert_threshold || 80}%</div>
                                    </div>
                                    <div className="p-4 bg-muted/50 rounded-lg">
                                        <div className="text-sm text-muted-foreground">Auto-approbation jusqu'à</div>
                                        <div className="text-2xl font-bold">{formatCFA(config?.auto_approval_limit || 0)}</div>
                                    </div>
                                </div>
                            </div>
                            {isAdmin && (
                                <div className="mt-6">
                                    <Button onClick={() => setConfigDialogOpen(true)}>
                                        <Settings className="h-4 w-4 mr-2" />
                                        Modifier la configuration
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Config Dialog */}
            <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Shield className="h-5 w-5" />
                            Configuration du Budget
                        </DialogTitle>
                        <DialogDescription>
                            Seuls les administrateurs peuvent modifier ces paramètres
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Budget annuel (FCFA)</Label>
                                <Input
                                    type="number"
                                    value={configForm.total_budget}
                                    onChange={(e) => setConfigForm(p => ({...p, total_budget: e.target.value}))}
                                />
                            </div>
                            <div>
                                <Label>Budget mensuel (FCFA)</Label>
                                <Input
                                    type="number"
                                    value={configForm.monthly_budget}
                                    onChange={(e) => setConfigForm(p => ({...p, monthly_budget: e.target.value}))}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Seuil d'alerte (%)</Label>
                                <Input
                                    type="number"
                                    value={configForm.alert_threshold}
                                    onChange={(e) => setConfigForm(p => ({...p, alert_threshold: e.target.value}))}
                                    min="50"
                                    max="100"
                                />
                            </div>
                            <div>
                                <Label>Limite auto-approbation (FCFA)</Label>
                                <Input
                                    type="number"
                                    value={configForm.auto_approval_limit}
                                    onChange={(e) => setConfigForm(p => ({...p, auto_approval_limit: e.target.value}))}
                                />
                            </div>
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleSaveConfig}>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default BudgetManagementPage;
